import React, {useState, useEffect, useCallback} from 'react';
import styled, {css} from 'styled-components';
import {FormattedMessage, useIntl} from 'react-intl';
import {Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import {BookmarkIcon, BOOKMARK_COLOR_ACTIVE, BOOKMARK_COLOR_DISABLED} from '../ai-chat/icons';
import {D} from '../ai-chat/design-tokens';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';

const {font} = tokensForThemeProvider.sky.sys;

const bodyFont = css`
	${font.body['1']}
`;

const hintFont = css`
	${font.detail['1']}
`;

const ComposerWrapper = styled.div`
	padding: 16px 16px 0;
	border-top: 1px solid ${D.inputBorder};
	background: ${D.bg};
	flex-shrink: 0;
`;

const ComposerRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 8px;
`;

const ButtonGroup = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
`;

const StyledTextArea = styled.textarea<{isFocused: boolean}>`
	${bodyFont}
	flex: 1;
	resize: none;
	outline: none;
	overflow-y: auto;
	scrollbar-width: none;
	padding: 10px 12px;
	background: ${D.bg};
	border: 1px solid ${({isFocused}) => (isFocused ? D.v2.accent : D.inputBorder)};
	box-shadow: ${({isFocused}) => (isFocused ? `0 0 0 1px ${D.v2.accent}` : 'none')};
	border-radius: 8px;
	color: ${D.v2.textPrimary};
	max-height: 120px;
	transition: border-color 100ms ease, box-shadow 100ms ease;

	&::-webkit-scrollbar {
		display: none;
	}

	&::placeholder {
		${bodyFont}
		color: ${D.textDisabled};
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const ActionBtn = styled.button`
	flex-shrink: 0;
	width: 44px;
	height: 44px;
	border-radius: 8px;
	border: none;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
`;

const SaveBtn = styled(ActionBtn)<{hidden: boolean}>`
	background: transparent;
	display: ${({hidden}) => (hidden ? 'none' : 'inline-flex')};
	transition: opacity 100ms ease;

	&:hover:not(:disabled) {
		opacity: 0.85;
	}

	&:active:not(:disabled) {
		opacity: 0.7;
	}

	&:disabled {
		cursor: default;
		opacity: 1;
	}
`;

const StopBtn = styled(ActionBtn)`
	background: ${D.v2.accent};
	color: ${D.bg};
	transition: opacity 100ms ease;

	&:hover {
		opacity: 0.85;
	}

	&:active {
		opacity: 0.7;
	}
`;

const SendBtn = styled(ActionBtn)<{active: boolean}>`
	background: ${({active}) => (active ? D.v2.accent : D.v2.btnInactiveBg)};
	color: ${({active}) => (active ? D.bg : D.v2.iconInactive)};
	cursor: ${({active}) => (active ? 'pointer' : 'default')};
	transition: opacity 100ms ease;

	&:hover {
		opacity: 0.85;
	}

	&:active {
		opacity: 0.7;
	}
`;

const FooterHint = styled.div`
	${hintFont}
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: ${D.v2.textSecondary};
	padding: 8px 0 12px;
`;

const Kbd = styled.kbd`
	${hintFont}
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	background: ${D.v2.surfaceHover};
	border: none;
	border-radius: 3px;
	color: ${D.v2.textPrimary};
`;

const Dot = styled.span`
	${hintFont}
	display: inline;
	color: ${D.v2.textPrimary};
`;

type DrawerFooterProps = {
	input: string;
	isStreaming: boolean;
	isHitlPending: boolean;
	apiKey: string;
	onInputChange: (value: string) => void;
	onSend: () => void;
	onStop: () => void;
	onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	inputRef: React.RefObject<HTMLTextAreaElement>;
	onSavePrompt: (promptText: string) => void;
};

const DrawerFooter = ({
	input,
	isStreaming,
	isHitlPending,
	apiKey,
	onInputChange,
	onSend,
	onStop,
	onKeyDown,
	inputRef,
	onSavePrompt,
}: DrawerFooterProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const {savedPromptTexts} = useAgentDrawerContext();
	const [isFocused, setIsFocused] = useState(false);
	const inputDisabled = isStreaming || !apiKey || isHitlPending;
	const canSend = !!apiKey && !isStreaming && !isHitlPending; // empty input intentionally allowed — product decision
	const canSavePrompt = !!input.trim();
	const isAlreadySaved = canSavePrompt && savedPromptTexts.has(input.trim());

	const autoResize = useCallback(() => {
		const el = inputRef.current;
		if (!el) return;
		el.style.height = 'auto';
		el.style.height = `${el.scrollHeight}px`;
	}, [inputRef]);

	useEffect(() => {
		autoResize();
	}, [input, autoResize]);

	return (
		<ComposerWrapper>
			<ComposerRow>
				<StyledTextArea
					ref={inputRef}
					value={input}
					onChange={e => onInputChange(e.target.value)}
					onKeyDown={onKeyDown}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					isFocused={isFocused && !inputDisabled}
					placeholder={formatMessage({id: 'nova.input.placeholder'})}
					disabled={inputDisabled}
					aria-label={
						isHitlPending
							? formatMessage({id: 'nova.input.hitl_label'})
							: formatMessage({id: 'nova.input.aria_label'})
					}
					rows={1}
					data-cy="agent-chat-input"
				/>
				<ButtonGroup>
					<SaveBtn
						type="button"
						onClick={() => onSavePrompt(input)}
						disabled={!canSavePrompt}
						hidden={isAlreadySaved}
						aria-label={formatMessage({id: 'nova.input.save_prompt'})}
						title={formatMessage({
							id: input.trim() ? 'nova.input.save_prompt_tooltip' : 'nova.input.save_prompt_tooltip_empty',
						})}
						data-cy="agent-save-prompt-button"
					>
						<BookmarkIcon color={canSavePrompt ? BOOKMARK_COLOR_ACTIVE : BOOKMARK_COLOR_DISABLED} />
					</SaveBtn>
					{isStreaming ? (
						<StopBtn
							type="button"
							onClick={onStop}
							aria-label={formatMessage({id: 'nova.input.stop'})}
							data-cy="agent-stop-button"
						>
							<Icon icon="stop" size="m" customColor={D.bg} />
						</StopBtn>
					) : (
						<SendBtn
							type="button"
							active={canSend}
							onClick={canSend ? onSend : undefined}
							disabled={!canSend}
							aria-label={formatMessage({id: 'nova.input.send'})}
							data-cy="agent-send-button"
						>
							<Icon icon="arrowRight" size="m" customColor={canSend ? D.bg : D.v2.iconInactive} />
						</SendBtn>
					)}
				</ButtonGroup>
			</ComposerRow>
			<FooterHint>
				<span>
					<FormattedMessage
						id="nova.input.footer_send"
						values={{
							enter: <Kbd>↲</Kbd>,
							dot: <Dot>•</Dot>,
							shift: (
								<>
									<Kbd>↑</Kbd>
									<Kbd>↲</Kbd>
								</>
							),
						}}
					/>
				</span>
				<span>
					<FormattedMessage id="nova.input.footer_data" />
				</span>
			</FooterHint>
		</ComposerWrapper>
	);
};

export default DrawerFooter;
